import {forkJoin as observableForkJoin } from 'rxjs';
import { Component, OnInit, ViewChild, AfterViewInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { UserRole } from './models/user-role';
import { UserPermission } from './models/user-permission';
import { UserRoleService } from './services/user-role.service';
import { UserPermissionService } from './services/user-permission.service';
import { CurrentUserService } from './services/current-user.service';
import { LoadingPanelService } from '../shared/loading-panel/loading-panel.service';
import { BizDataGridDirective } from '../shared/grid/biz-grid.directive';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { SvgIconComponent } from '../shared/icons/svg-icon.component';
import { ChangeLogLinkComponent } from '../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { DxoColumnFixingModule, DxiColumnModule, DxoHeaderFilterModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';

import { BizGridFiltervalues } from '../shared/grid/biz-grid-filtervalues';

@Component({
    templateUrl: './user-role-crud.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PermissionRequiredDirective, SvgIconComponent, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoColumnFixingModule, DxiColumnModule, DxoHeaderFilterModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})

export class UserRoleCrudComponent implements OnInit, AfterViewInit {

    public readonly bizGridFiltervalues = inject(BizGridFiltervalues);
    private readonly router = inject(Router);
    private readonly userRoleService = inject(UserRoleService);
    private readonly userPermissionService = inject(UserPermissionService);
    private readonly loadingPanelService = inject(LoadingPanelService);
    private readonly currentUserService = inject(CurrentUserService);
    
    @ViewChild(BizDataGridDirective, { static: true }) private datagrid: BizDataGridDirective;

    public roles: UserRole[] = [];
    public availablePermissions: UserPermission[] = [];
    public createAllowed = false;
    public editAllowed = false;
    public deleteAllowed = false;
    public showLogAllowed = false;
    public selectedRow: UserRole = null;

    @ViewChild('columnPermissions', { static: true }) columnPermissions: any;

    ngOnInit() {
        const promises = [];
        promises.push(this.userRoleService.getUserRoles().then(roles => {
            this.roles = roles.sort((r1, r2) => r1.RoleName.localeCompare(r2.RoleName));
        }));
        promises.push(this.userPermissionService.getUserPermissions().then(permissions => {
            this.availablePermissions = permissions.sort((p1, p2) => p1.PermissionName.localeCompare(p2.PermissionName))
        }));

        this.loadingPanelService.showLoadingPanelDirect('Loading User Roles...');
        observableForkJoin(promises).subscribe(() => {
            this.setUserRoleReference();
            this.initPermissions();
            this.loadingPanelService.hideLoadingPanel();
        });
    }

    ngAfterViewInit() {
        if (this.currentUserService.hasPermission('UserRoles_Action_Edit')) {
            this.datagrid.rowDoubleClick.subscribe((row: UserRole) => {
                this.showRole(row);
            });
        }
    }

    createNew() {
        void this.router.navigate(['user/roles/details', { createNew: true }]);
    }

    showRole(role: UserRole) {
        if (role) {
            void this.router.navigate(['user/roles/details', { id: role.RoleId }]);
        }
    }

    selectionChanged(e: any) {
        this.selectedRow = null;
        if (e && e.selectedRowsData && (e.selectedRowsData.length === 1)) {
            this.selectedRow = e.selectedRowsData[0];
        }
    }

    initPermissions() {
        this.createAllowed = this.currentUserService.hasPermission('UserRoles_Action_Create');
        this.editAllowed = this.currentUserService.hasPermission('UserRoles_Action_Edit');
        this.deleteAllowed = this.currentUserService.hasPermission('UserRoles_Action_Delete');
        this.showLogAllowed = this.currentUserService.hasPermission('UserRole_Action_ShowLog');
    }

    setUserRoleReference() {
        if (this.roles && this.availablePermissions) {
            this.roles.forEach(ug => {
                ug.Permissions.forEach((role, index) => {
                    ug.Permissions[index] = this.availablePermissions.find(r => r.PermissionId === role.PermissionId);
                });
            });
        }
    }

    // Sorting and filtering roles
    calculateSortValue(rowData: { Permissions: UserPermission[] }) {
        // Sort by name - concatenate to a string
        const permissions: UserPermission[] = rowData.Permissions;
        return permissions.sort((r1, r2) => r1.PermissionName.localeCompare(r2.PermissionName)).map((r) => r.PermissionName).join('');
    }

    getPermissionsHeaderFilterData = () =>
        (this.roles ?? []) // Ensure userGroups is defined; default to an empty array if undefined
        .flatMap(e => {
            const permissions = e.Permissions?.map(permission => permission.PermissionName) || [];
            return permissions.length > 0 ? permissions : ['(Blanks)'];
            }) // Flatten the RoleName values from all Roles, or add '(blanks)' if no permissions
            .filter((name, idx, self) => self.indexOf(name) === idx) // Remove duplicates
            .sort((a, b) => a.localeCompare(b)) // Sort the names alphabetically
            .map(permissionName => ({
                text: permissionName,
                value: permissionName
            }))
    ;

    calculateArrayFilterExpression(filterValue: any) {
        // Custom filter for array fields
        return (rowData: any) => {
            if (filterValue === '(Blanks)') {
                // Check if there are no permissions
                return !rowData.Permissions || rowData.Permissions.length === 0;
            }
            return rowData.Permissions?.some((permission: any) =>
                permission.PermissionName?.toLowerCase().includes(filterValue.toLowerCase())
            );
        };
    }
}
